import { useCallback, useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import esLocale from "@fullcalendar/core/locales/es";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const DAY_DATE = {
  lunes: "2024-01-01", martes: "2024-01-02", "miércoles": "2024-01-03", miercoles: "2024-01-03",
  jueves: "2024-01-04", viernes: "2024-01-05", "sábado": "2024-01-06", sabado: "2024-01-06",
};
const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const PALETTE = ["#1e3a8a", "#0369a1", "#047857", "#b45309", "#be123c", "#6d28d9", "#0f766e", "#a21caf"];

const colorFor = (text) => {
  let h = 0;
  for (const c of String(text || "")) h = (h * 31 + c.charCodeAt(0)) % 997;
  return PALETTE[h % PALETTE.length];
};

const norm = (d) => String(d || "").trim().toLowerCase();
const dateForDay = (day) => DAY_DATE[norm(day)];

export default function ScheduleCalendar({ canWrite, refreshKey }) {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    api.get("/r/schedules").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    api.get("/r/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/r/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, []);

  const events = items
    .filter((s) => dateForDay(s.day) && s.start_time)
    .map((s) => ({
      id: s.id,
      title: `${s.subject_name || "Materia"} · ${s.course}${s.section ? " " + s.section : ""}`,
      start: `${dateForDay(s.day)}T${s.start_time.length === 5 ? s.start_time + ":00" : s.start_time}`,
      end: s.end_time ? `${dateForDay(s.day)}T${s.end_time.length === 5 ? s.end_time + ":00" : s.end_time}` : undefined,
      backgroundColor: colorFor(s.subject_name),
      borderColor: colorFor(s.subject_name),
      extendedProps: { teacher: s.teacher_name },
    }));

  const persist = async (id, payload) => {
    try {
      await api.put(`/r/schedules/${id}`, payload);
      toast.success("Horario actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const onEventChange = (info) => {
    const d = info.event.start;
    const day = DAYS[d.getDay() - 1] || "Lunes";
    const hhmm = (x) => x ? `${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}` : "";
    persist(info.event.id, { day, start_time: hhmm(info.event.start), end_time: hhmm(info.event.end) });
  };

  const onEventClick = (info) => {
    const s = items.find((x) => x.id === info.event.id);
    if (!s) return;
    setEditing(s);
    setForm({
      course: s.course || "", section: s.section || "A", subject_id: s.subject_id || "",
      day: s.day || "Lunes", start_time: s.start_time || "", end_time: s.end_time || "",
      teacher_name: s.teacher_name || "",
    });
  };

  const saveEdit = () => {
    const subj = subjects.find((x) => x.id === form.subject_id);
    persist(editing.id, { ...form, subject_name: subj?.name || "" });
    setEditing(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="schedule-calendar">
      <FullCalendar
        plugins={[timeGridPlugin, interactionPlugin, listPlugin]}
        initialView="timeGridWeek"
        initialDate="2024-01-01"
        locale={esLocale}
        headerToolbar={{ left: "", center: "", right: "timeGridWeek,listWeek" }}
        buttonText={{ week: "Semana", list: "Agenda" }}
        allDaySlot={false}
        hiddenDays={[0]}
        slotMinTime="06:00:00"
        slotMaxTime="19:00:00"
        dayHeaderFormat={{ weekday: "long" }}
        slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: true }}
        eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: true }}
        events={events}
        editable={canWrite}
        eventDrop={onEventChange}
        eventResize={onEventChange}
        eventClick={onEventClick}
        height="auto"
        nowIndicator={false}
      />
      {items.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6">Sin horarios todavía. Importa un Excel o crea desde la vista Lista.</p>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="schedule-edit-modal">
          <DialogHeader><DialogTitle>Editar horario</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Curso</Label>
                <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
                  <SelectTrigger data-testid="sched-edit-course"><SelectValue placeholder="Curso" /></SelectTrigger>
                  <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sección</Label>
                <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                  <SelectTrigger data-testid="sched-edit-section"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Materia</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="sched-edit-subject"><SelectValue placeholder="Materia" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Día</Label>
                <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
                  <SelectTrigger data-testid="sched-edit-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Inicio</Label><Input type="time" data-testid="sched-edit-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Fin</Label><Input type="time" data-testid="sched-edit-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Maestro</Label><Input data-testid="sched-edit-teacher" value={form.teacher_name} onChange={(e) => setForm({ ...form, teacher_name: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="sched-edit-cancel" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button data-testid="sched-edit-save" className="bg-blue-800 hover:bg-blue-700" onClick={saveEdit} disabled={!canWrite}>Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
